"use client"

import {useMemo} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {DashboardCurrent as Dashboard} from "@devplaninc/codeup";
import {dashboardKeys} from "@/hooks/queries/queryKeys";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {dashboardPromptAPI} from "@/app/apiPaths";
import {jr} from "@/hooks/queries/response-handler";
import {GetDashboardGenerationPromptResponse} from "@/pb/api/devplan/services/web/company";
import type {DashboardCreateInput, DashboardFavoriteInput, DashboardMetadataInput, DashboardRegenerateInput, DashboardVersionRestoreInput} from "@/hooks/queries/stores/dashboardsStore";
import {dashboardA2UITitle} from "@/lib/dashboards/a2ui";
import {dashboardLastUpdated} from "@/lib/dashboards/presentation";

export type {DashboardCreateInput, DashboardFavoriteInput, DashboardMetadataInput, DashboardRegenerateInput, DashboardVersionRestoreInput};

export function useDashboards(companyId: number, search: string, archived = false) {
  const fetchDashboards = useBoundStore(s => s.fetchDashboards);
  const dashboardRecord = useBoundStore(s => s.dashboards[companyId] || {});
  const activeQuery = useQuery({
    queryKey: dashboardKeys.list(companyId, false),
    queryFn: () => fetchDashboards(companyId, false),
  });
  const archivedQuery = useQuery({
    queryKey: dashboardKeys.list(companyId, true),
    queryFn: () => fetchDashboards(companyId, true),
    enabled: archived,
  });
  const normalizedSearch = search.trim().toLowerCase();
  const dashboards = useMemo(() => {
    const all = Object.values(dashboardRecord).filter(dashboard => archived || !dashboard.head?.archivedAt).sort((a, b) => {
      const archivedOrder = Number(Boolean(a.head?.archivedAt)) - Number(Boolean(b.head?.archivedAt));
      if (archivedOrder !== 0) return archivedOrder;
      const favoriteOrder = Number(Boolean(b.head?.favorite)) - Number(Boolean(a.head?.favorite));
      if (favoriteOrder !== 0) return favoriteOrder;
      const aTime = dashboardLastUpdated(a)?.getTime() ?? 0;
      const bTime = dashboardLastUpdated(b)?.getTime() ?? 0;
      return bTime - aTime || dashboardId(a).localeCompare(dashboardId(b));
    });
    if (!normalizedSearch) return all;
    const details = (dashboard: Dashboard) => dashboard.version?.details;
    return all.filter(dashboard => [
      details(dashboard)?.title ?? "",
      dashboardA2UITitle(details(dashboard)?.document?.json) ?? "",
      details(dashboard)?.prompt ?? "",
      details(dashboard)?.document?.json ?? "",
    ].some(value => value.toLowerCase().includes(normalizedSearch)));
  }, [archived, dashboardRecord, normalizedSearch]);

  return {
    dashboards,
    loading: activeQuery.isPending || (archived && archivedQuery.isPending),
    error: activeQuery.error ?? archivedQuery.error,
    refetch: async () => {
      const activeResult = await activeQuery.refetch();
      if (archived) await archivedQuery.refetch();
      return activeResult;
    },
  };
}

export function useDashboardVersions(companyId: number, dashboardId: string) {
  const fetchDashboardVersions = useBoundStore(s => s.fetchDashboardVersions);
  const cached = useBoundStore(s => s.dashboardVersions[companyId]?.[dashboardId] || []);
  const query = useQuery({
    queryKey: dashboardKeys.versions(companyId, dashboardId),
    queryFn: () => fetchDashboardVersions(companyId, dashboardId),
    enabled: Boolean(dashboardId),
  });
  return {versions: cached.length ? cached : (query.data || []), loading: query.isPending, error: query.error, refetch: query.refetch};
}

export function useDashboard(companyId: number, dashboardId: string) {
  const fetchDashboard = useBoundStore(s => s.fetchDashboard);
  const cached = useBoundStore(s => s.dashboards[companyId]?.[dashboardId]);
  const query = useQuery({
    queryKey: dashboardKeys.detail(companyId, dashboardId),
    queryFn: () => fetchDashboard(companyId, dashboardId),
  });
  return {
    dashboard: cached ?? query.data,
    loading: query.isPending,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useDashboardPrompt(companyId: number, dashboardId: string, enabled: boolean) {
  return useQuery({
    queryKey: dashboardKeys.prompt(companyId, dashboardId),
    queryFn: async () => {
      const response = await fetch(dashboardPromptAPI(companyId, dashboardId));
      return GetDashboardGenerationPromptResponse.fromJSON(await jr(response));
    },
    enabled: enabled && Boolean(dashboardId),
    staleTime: 0,
  });
}

function dashboardId(dashboard: Dashboard) {
  return dashboard.head?.id ?? "";
}

export function useDashboardMutations(companyId: number) {
  const queryClient = useQueryClient();
  const createDashboard = useBoundStore(s => s.createDashboard);
  const regenerateDashboard = useBoundStore(s => s.regenerateDashboard);
  const updateDashboard = useBoundStore(s => s.updateDashboard);
  const refreshDashboard = useBoundStore(s => s.refreshDashboard);
  const updateDashboardMetadata = useBoundStore(s => s.updateDashboardMetadata);
  const archiveDashboard = useBoundStore(s => s.archiveDashboard);
  const restoreDashboard = useBoundStore(s => s.restoreDashboard);
  const activateDashboardVersion = useBoundStore(s => s.activateDashboardVersion);
  const restoreDashboardVersion = useBoundStore(s => s.restoreDashboardVersion);
  const setDashboardFavorite = useBoundStore(s => s.setDashboardFavorite);
  const invalidate = async (dashboardId?: string) => {
    await queryClient.invalidateQueries({queryKey: dashboardKeys.lists(companyId)});
    if (dashboardId) {
      await queryClient.invalidateQueries({queryKey: dashboardKeys.detail(companyId, dashboardId)});
      await queryClient.invalidateQueries({queryKey: dashboardKeys.versions(companyId, dashboardId)});
    }
  };

  return {
    createDashboard: useMutation({
      mutationFn: (input: DashboardCreateInput) => createDashboard(companyId, input),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    regenerateDashboard: useMutation({
      mutationFn: (input: DashboardRegenerateInput) => regenerateDashboard(companyId, input),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    updateDashboard: useMutation({
      mutationFn: (input: DashboardRegenerateInput) => updateDashboard(companyId, input),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    refreshDashboard: useMutation({
      mutationFn: (dashboardId: string) => refreshDashboard(companyId, dashboardId),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    updateDashboardMetadata: useMutation({
      mutationFn: (input: DashboardMetadataInput) => updateDashboardMetadata(companyId, input),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    archiveDashboard: useMutation({
      mutationFn: (dashboardId: string) => archiveDashboard(companyId, dashboardId),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    restoreDashboard: useMutation({
      mutationFn: (dashboardId: string) => restoreDashboard(companyId, dashboardId),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    activateDashboardVersion: useMutation({
      mutationFn: (input: DashboardVersionRestoreInput) => activateDashboardVersion(companyId, input),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    restoreDashboardVersion: useMutation({
      mutationFn: (input: DashboardVersionRestoreInput) => restoreDashboardVersion(companyId, input),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
    setDashboardFavorite: useMutation({
      mutationFn: (input: DashboardFavoriteInput) => setDashboardFavorite(companyId, input),
      onSuccess: dashboard => invalidate(dashboardId(dashboard)),
    }),
  };
}
